import * as React from 'react'
import { useCallback } from 'react'
import { Formik, type FormikErrors } from 'formik'

import { toast } from '@sales-center/components'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import { cancelOpportunityServices } from '@user/store/opportunity'
import {
  useGetCancelReasonsOnMount,
  useSchedulingEnabled,
} from '@user/store/scheduling'
import { selectAllCancelReasons } from '@user/store/scheduling/cancelReasonsSlice'
import {
  closeDialogActionCreator as closeDialog,
  useCWQDialogContext,
} from '@user/components/SoldServices'

import Dialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import { useTheme } from '@mui/material/styles'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'

import { CancelServicesSection } from './CancelServicesSection'
import {
  type CancelServicesFormState,
  cancelServicesInitialValues,
} from './cancelServicesFormConfig'

type Props = {
  opportunityId: number
}

export function CancelServicesDialog(props: Props) {
  const { opportunityId } = props
  const theme = useTheme()
  const [{ activeDialog }, ctxDispatch] = useCWQDialogContext()
  const open = activeDialog === 'cancelServices'

  const onClose = useCallback(() => ctxDispatch(closeDialog()), [ctxDispatch])

  return (
    <Dialog fullWidth open={open} maxWidth="xs" onClose={onClose}>
      <DialogTitle
        fontSize="20px"
        color={theme.palette.text.primary}
        sx={{
          p: 3,
          pb: '16px',
        }}
      >
        Cancel Services
      </DialogTitle>
      {open && (
        <CancelServicesDialogBody
          opportunityId={opportunityId}
          onClose={onClose}
        />
      )}
    </Dialog>
  )
}

type BodyProps = {
  opportunityId: number
  onClose: () => void
}

function CancelServicesDialogBody(props: BodyProps) {
  const { opportunityId, onClose } = props
  const dispatch = useAppDispatch()

  const schedulingEnabled = useSchedulingEnabled()
  const cancelReasons = useAppSelector(selectAllCancelReasons)
  useGetCancelReasonsOnMount(schedulingEnabled)

  const requireCancelReason = cancelReasons.length > 0

  const validate = useCallback(
    (model: CancelServicesFormState) => {
      const errors: FormikErrors<CancelServicesFormState> = {}
      if (
        model.selectedServiceIds.length > 0 &&
        model.cancelConfirmation.trim().toUpperCase() !== 'CANCEL'
      ) {
        errors.cancelConfirmation = 'Required'
      }
      if (
        requireCancelReason &&
        model.selectedServiceIds.length > 0 &&
        model.notServicedReason.trim() === ''
      ) {
        errors.notServicedReason = 'Required'
      }
      return errors
    },
    [requireCancelReason],
  )

  const onSubmit = useCallback(
    async (model: CancelServicesFormState) => {
      if (model.selectedServiceIds.length === 0) {
        onClose()
        return
      }
      const result = await dispatch(
        cancelOpportunityServices({
          opportunityId,
          serviceIds: model.selectedServiceIds.map(Number),
          notServicedReason: model.notServicedReason.trim() || undefined,
        }),
      )
      if (cancelOpportunityServices.fulfilled.match(result)) {
        toast.success('Services canceled')
        onClose()
      } else {
        toast.error('Unable to cancel services')
      }
    },
    [dispatch, opportunityId, onClose],
  )

  return (
    <Formik
      validate={validate}
      onSubmit={onSubmit}
      initialValues={cancelServicesInitialValues}
    >
      {({ submitForm, isSubmitting, values }) => (
        <>
          <DialogContent sx={{ pt: 0, pb: '20px', px: 3 }}>
            <CancelServicesSection
              opportunityId={opportunityId}
              showCancelReason={requireCancelReason}
              showEmptyAlert
              cancelReasons={cancelReasons}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={onClose}
              data-test-id="cancel-services-cancel-button"
            >
              Close
            </Button>
            <Button
              variant="contained"
              disabled={isSubmitting || values.selectedServiceIds.length === 0}
              onClick={submitForm}
              data-test-id="cancel-services-save-button"
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Formik>
  )
}
